import React, { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArticleCard from '../../Components/ArticleCard';
import Loading from '../../Components/Loading';
import BackButton from '../../Components/BackButton';
import { useCachedGet } from '../../Services/useCachedGet';
import EntityAvatar from '../../Components/Shared/EntityAvatar';
import { gsap, SplitText, useGSAP, prefersReducedMotion } from '../../lib/gsap';
import { useReveal } from '../../Components/Fx/useReveal';

const EventPage = () => {
   const { eventId } = useParams();
   const { data: event, loading: eventLoading } = useCachedGet<any>(`${process.env.REACT_APP_API}/event/${eventId}`);
   const { data: eventNews, loading: newsLoading } = useCachedGet<any[]>(
      `${process.env.REACT_APP_API}/event/${eventId}/articles`
   );
   const { data: allEntities } = useCachedGet<any[]>(`${process.env.REACT_APP_API}/entities`);
   const scope = useRef<HTMLDivElement>(null);
   const ready = !(eventLoading || newsLoading || !event || !eventNews);

   useGSAP(
      () => {
         if (!ready || prefersReducedMotion()) return;
         const split = SplitText.create('[data-event-title]', {
            type: 'lines,words',
            mask: 'lines',
            autoSplit: true,
            onSplit: self => gsap.from(self.words, { yPercent: 110, stagger: 0.04, duration: 1.3 }),
         });
         gsap.fromTo('[data-event-cover]', { scale: 1.25 }, { scale: 1, duration: 2.4, ease: 'expo.out' });
         gsap.to('[data-event-cover]', {
            yPercent: 18,
            ease: 'none',
            scrollTrigger: { trigger: '[data-event-header]', start: 'top top', end: 'bottom top', scrub: true },
         });
         return () => split.revert();
      },
      { scope, dependencies: [ready] }
   );

   useReveal(scope, [ready]);

   if (!ready) return <Loading label="Opening file" />;

   const linkedEntities = (allEntities || []).filter((entity: any) => event.entityIds?.includes(entity.id));

   return (
      <div ref={scope}>
         <header data-event-header className="relative flex min-h-[88vh] items-end overflow-hidden">
            {event.cover && (
               <div
                  className="absolute inset-0 overflow-hidden"
                  style={{
                     maskImage: 'linear-gradient(to bottom, black 35%, transparent 100%)',
                     WebkitMaskImage: 'linear-gradient(to bottom, black 35%, transparent 100%)',
                  }}
               >
                  <img
                     data-event-cover
                     src={event.cover}
                     alt=""
                     className="h-full w-full object-cover grayscale contrast-125 brightness-50"
                  />
                  <div className="absolute inset-0 bg-signal mix-blend-multiply opacity-40" />
               </div>
            )}
            <div
               className="absolute inset-0 bg-gradient-to-r from-ink-950/70 via-ink-950/30 to-transparent"
               style={{
                  maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
                  WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
               }}
            />

            <div className="page relative w-full pb-16 pt-32">
               <div className="mb-10">
                  <BackButton location="/" label="All cases" />
               </div>
               <p className="eyebrow mb-5 text-signal-soft">Case file</p>
               <h1 data-event-title className="max-w-5xl font-display text-5xl leading-[0.95] tracking-tightest md:text-7xl xl:text-8xl">
                  {event.title}
               </h1>
               {event.tags?.length > 0 && (
                  <ul className="mt-8 flex flex-wrap gap-2">
                     {event.tags.map((tag: any) => (
                        <li key={tag.id || tag.label} className="chip">
                           {tag.label}
                        </li>
                     ))}
                  </ul>
               )}
            </div>
         </header>

         <div className="page mt-16 grid grid-cols-1 gap-16 lg:grid-cols-12">
            <section className="lg:col-span-8">
               <p className="eyebrow mb-6" data-reveal>
                  Summary
               </p>
               <p
                  className="font-display text-2xl leading-snug text-bone first-letter:float-left first-letter:mr-3 first-letter:text-7xl first-letter:leading-[0.8] first-letter:text-signal md:text-[2rem]"
                  data-reveal
               >
                  {event.summary}
               </p>
            </section>

            <aside className="lg:col-span-4">
               <div className="lg:sticky lg:top-24 space-y-10">
                  <div className="card" data-reveal>
                     <p className="eyebrow mb-4">Dossier</p>
                     <dl className="space-y-3 font-mono text-xs">
                        <div className="flex justify-between gap-4 border-b border-white/[0.06] pb-3">
                           <dt className="text-bone-dim">Sources</dt>
                           <dd className="text-bone">{String(eventNews.length).padStart(2, '0')}</dd>
                        </div>
                        <div className="flex justify-between gap-4 border-b border-white/[0.06] pb-3">
                           <dt className="text-bone-dim">Parties named</dt>
                           <dd className="text-bone">{String(linkedEntities.length).padStart(2, '0')}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                           <dt className="text-bone-dim">File ID</dt>
                           <dd className="truncate text-bone">{String(event.id).slice(0, 8)}</dd>
                        </div>
                     </dl>
                  </div>

                  {linkedEntities.length > 0 && (
                     <div data-reveal>
                        <p className="eyebrow mb-4">In this case</p>
                        <div className="space-y-1">
                           {linkedEntities.map((entity: any) => (
                              <Link
                                 to={`/profile/${entity.id}`}
                                 key={entity.id}
                                 className="group -mx-3 flex items-center justify-between rounded-sm px-3 py-2 transition-colors hover:bg-white/[0.04]"
                              >
                                 <EntityAvatar entity={entity} />
                                 <span className="text-bone-dim transition-transform group-hover:translate-x-1 group-hover:text-signal">
                                    →
                                 </span>
                              </Link>
                           ))}
                        </div>
                     </div>
                  )}
               </div>
            </aside>
         </div>

         <section className="page mt-28">
            <div className="mb-10 flex items-end justify-between" data-reveal>
               <h2 className="headline mb-0">Sources</h2>
               <span className="font-mono text-xs text-bone-dim">{eventNews.length} documents</span>
            </div>
            {eventNews.length ? (
               <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {eventNews.map((article, i) => (
                     <div key={article.id} data-reveal>
                        <ArticleCard article={article} index={i} />
                     </div>
                  ))}
               </div>
            ) : (
               <p className="text-bone-muted">No articles yet for this event.</p>
            )}
         </section>
      </div>
   );
};

export default EventPage;
